/**
 * Razorpay checkout helper.
 * Loads the Razorpay checkout script lazily and opens the modal.
 */
import { api, formatApiError } from "./api";
import { toast } from "sonner";
import { trackMeta } from "./metaPixel";

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";
let _scriptPromise = null;

function loadScript() {
    if (_scriptPromise) return _scriptPromise;
    _scriptPromise = new Promise((resolve, reject) => {
        if (typeof window !== "undefined" && window.Razorpay) {
            resolve(true);
            return;
        }
        const s = document.createElement("script");
        s.src = RAZORPAY_SCRIPT_URL;
        s.async = true;
        s.onload = () => resolve(true);
        s.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
        document.body.appendChild(s);
    });
    return _scriptPromise;
}

/**
 * Start a subscription checkout for the given plan.
 * @param {string} planId  one of "silver" | "gold" | "platinum"
 * @param {Object} [opts]
 * @param {boolean} [opts.useBonusBalance=false]  apply the user's non-withdrawable bonus credits towards this plan
 * @returns {Promise<{ok: boolean, plan?: string, expires_at?: string}>}
 */
export async function startSubscriptionCheckout(planId, { useBonusBalance = false } = {}) {
    let orderRes;
    try {
        const { data } = await api.post("/subscriptions/create-order", {
            plan_id: planId,
            use_bonus_balance: useBonusBalance,
        });
        orderRes = data;
    } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not create order");
        return { ok: false };
    }

    // Plan was fully covered by bonus credits — no Razorpay step needed.
    if (orderRes.fully_covered_by_bonus) {
        // Bonus-covered purchase — fire Purchase directly (backend already activated the plan)
        try {
            trackMeta("Purchase",
                { content_ids: [planId], content_type: "product", value: (orderRes.amount || 0) / 100, currency: orderRes.currency || "INR", num_items: 1, payment_method: "bonus_credits" },
                { email: orderRes.user?.email, phone: orderRes.user?.phone || orderRes.user?.contact }
            );
        } catch { /* silent */ }
        toast.success(orderRes.message || `Plan activated using bonus credits!`);
        return { ok: true, fully_covered_by_bonus: true, ...orderRes };
    }

    // InitiateCheckout — user chose a plan and Razorpay is about to open
    try {
        trackMeta("InitiateCheckout",
            { content_ids: [planId], content_type: "product", content_name: orderRes.plan?.name, value: (orderRes.amount || 0) / 100, currency: orderRes.currency || "INR", num_items: 1 },
            { email: orderRes.user?.email, phone: orderRes.user?.phone || orderRes.user?.contact }
        );
    } catch { /* silent */ }

    try {
        await loadScript();
    } catch (e) {
        toast.error("Could not load payment gateway. Please refresh and try again.");
        return { ok: false };
    }

    return new Promise((resolve) => {
        const opts = {
            key: orderRes.key_id,
            amount: orderRes.amount,
            currency: orderRes.currency,
            name: "Global Property Mart",
            description: `${orderRes.plan.name} Subscription (30 days)${orderRes.bonus_used ? ` · ₹${orderRes.bonus_used} bonus applied` : ""}`,
            image: "/gpm-logo.png",
            order_id: orderRes.order_id,
            prefill: {
                name: orderRes.user?.name || "",
                email: orderRes.user?.email || "",
                contact: orderRes.user?.contact || orderRes.user?.phone || "",
            },
            theme: { color: "#DAA520" },
            notes: { plan_id: orderRes.plan.id },
            handler: async (response) => {
                try {
                    const verify = await api.post("/subscriptions/verify-payment", {
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                        plan_id: planId,
                    });
                    // Purchase event — verified payment
                    try {
                        trackMeta("Purchase",
                            { content_ids: [planId], content_type: "product", content_name: orderRes.plan?.name, value: (orderRes.amount || 0) / 100, currency: orderRes.currency || "INR", num_items: 1, order_id: response.razorpay_order_id },
                            { email: orderRes.user?.email, phone: orderRes.user?.phone || orderRes.user?.contact }
                        );
                    } catch { /* silent */ }
                    toast.success(`${orderRes.plan.name} plan activated successfully!`);
                    resolve({ ok: true, ...verify.data });
                } catch (err) {
                    toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed");
                    resolve({ ok: false });
                }
            },
            modal: {
                ondismiss: () => {
                    toast("Payment cancelled");
                    resolve({ ok: false, cancelled: true });
                },
            },
        };
        const rzp = new window.Razorpay(opts);
        rzp.on("payment.failed", (resp) => {
            const reason = resp?.error?.description || "Payment failed";
            toast.error(reason);
            resolve({ ok: false, error: reason });
        });
        rzp.open();
    });
}



/**
 * Start a Boost checkout for a specific property.
 * @param {string} planId   one of "basic-boost" | "premium-lead" | "advance-boost" | "ultimate-lead"
 * @param {string} propertyId  the property to feature/boost
 * @returns {Promise<{ok: boolean, boost?: object, cancelled?: boolean}>}
 */
export async function startBoostCheckout(planId, propertyId) {
    let orderRes;
    try {
        const { data } = await api.post("/boost/create-order", {
            plan_id: planId,
            property_id: propertyId,
        });
        orderRes = data;
    } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not create boost order");
        return { ok: false };
    }

    try {
        await loadScript();
    } catch (e) {
        toast.error("Could not load payment gateway. Please refresh and try again.");
        return { ok: false };
    }

    return new Promise((resolve) => {
        const opts = {
            key: orderRes.key_id,
            amount: orderRes.amount,
            currency: orderRes.currency,
            name: "Global Property Mart",
            description: `${orderRes.plan.name} — ${orderRes.property.title}`,
            image: "/gpm-logo.png",
            order_id: orderRes.order_id,
            prefill: {
                name: orderRes.user?.name || "",
                email: orderRes.user?.email || "",
                contact: orderRes.user?.phone || "",
            },
            theme: { color: "#DAA520" },
            notes: { kind: "boost", plan_id: orderRes.plan.id, property_id: orderRes.property.id },
            handler: async (response) => {
                try {
                    const verify = await api.post("/boost/verify-payment", {
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                        plan_id: planId,
                    });
                    toast.success(`${orderRes.plan.name} activated! Your property is now featured.`);
                    resolve({ ok: true, ...verify.data });
                } catch (err) {
                    toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed");
                    resolve({ ok: false });
                }
            },
            modal: {
                ondismiss: () => {
                    toast("Payment cancelled");
                    resolve({ ok: false, cancelled: true });
                },
            },
        };
        const rzp = new window.Razorpay(opts);
        rzp.on("payment.failed", (resp) => {
            const reason = resp?.error?.description || "Payment failed";
            toast.error(reason);
            resolve({ ok: false, error: reason });
        });
        rzp.open();
    });
}


/**
 * Start ₹199 Feature Property payment for a specific property.
 * On success (verified server-side), the request moves to Admin approval queue.
 * @param {string} propertyId
 * @returns {Promise<{ok:boolean, cancelled?:boolean, message?:string, request_id?:string}>}
 */
export async function startFeaturePayment(propertyId) {
    let orderRes;
    try {
        const { data } = await api.post("/feature-requests/create-order", { property_id: propertyId });
        orderRes = data;
    } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not create payment order");
        return { ok: false };
    }

    try {
        await loadScript();
    } catch {
        toast.error("Could not load payment gateway. Please refresh and try again.");
        return { ok: false };
    }

    return new Promise((resolve) => {
        const opts = {
            key: orderRes.key_id,
            amount: orderRes.amount,
            currency: orderRes.currency,
            name: "Global Property Mart",
            description: `Feature Property — ${orderRes.property.title} (30 days)`,
            image: "/gpm-logo.png",
            order_id: orderRes.order_id,
            prefill: {
                name: orderRes.user?.name || "",
                email: orderRes.user?.email || "",
                contact: orderRes.user?.phone || "",
            },
            theme: { color: "#DAA520" },
            notes: { kind: "feature_property", property_id: orderRes.property.id },
            handler: async (response) => {
                try {
                    const verify = await api.post("/feature-requests/verify-payment", {
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                        property_id: propertyId,
                    });
                    resolve({ ok: true, ...verify.data });
                } catch (err) {
                    toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed");
                    resolve({ ok: false });
                }
            },
            modal: {
                ondismiss: () => {
                    toast("Payment cancelled");
                    resolve({ ok: false, cancelled: true });
                },
            },
        };
        const rzp = new window.Razorpay(opts);
        rzp.on("payment.failed", (resp) => {
            const reason = resp?.error?.description || "Payment failed";
            toast.error(reason);
            resolve({ ok: false, error: reason });
        });
        rzp.open();
    });
}

/**
 * Pay-per-listing (₹99) checkout — buys ONE extra property-listing credit.
 * On success, the backend flips a credit to `available` which lets the user
 * publish exactly one more property. Consumed FIFO on the next property create.
 */
export async function startListingCreditCheckout() {
    let orderRes;
    try {
        const { data } = await api.post("/listings/credits/order");
        orderRes = data;
    } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not create payment order");
        return { ok: false };
    }

    try {
        await loadScript();
    } catch {
        toast.error("Could not load payment gateway. Please refresh and try again.");
        return { ok: false };
    }

    return new Promise((resolve) => {
        const opts = {
            key: orderRes.key_id,
            amount: orderRes.amount,
            currency: orderRes.currency,
            name: "Global Property Mart",
            description: `Extra Property Listing (₹${orderRes.amount_inr})`,
            image: "/gpm-logo.png",
            order_id: orderRes.order_id,
            prefill: {
                name: orderRes.user?.name || "",
                email: orderRes.user?.email || "",
                contact: orderRes.user?.phone || "",
            },
            theme: { color: "#DAA520" },
            notes: { kind: "listing_credit" },
            handler: async (response) => {
                try {
                    const verify = await api.post("/listings/credits/verify", {
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                    });
                    try { trackMeta("Purchase", { value: orderRes.amount_inr, currency: "INR", content_name: "listing_credit" }); } catch { /* ignore */ }
                    resolve({ ok: true, ...verify.data });
                } catch (err) {
                    toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed");
                    resolve({ ok: false });
                }
            },
            modal: {
                ondismiss: () => {
                    toast("Payment cancelled");
                    resolve({ ok: false, cancelled: true });
                },
            },
        };
        const rzp = new window.Razorpay(opts);
        rzp.on("payment.failed", (resp) => {
            const reason = resp?.error?.description || "Payment failed";
            toast.error(reason);
            resolve({ ok: false, error: reason });
        });
        rzp.open();
    });
}


/**
 * GPM Partner ₹1,000 onboarding checkout. Requires the user has already registered
 * with role='gpm_partner' and verified their email.
 * On success (verified server-side): activates Partner, issues Partner ID, sets 12-month validity.
 * @returns {Promise<{ok:boolean, cancelled?:boolean, partner_id?:string, referral_link?:string}>}
 */
export async function startPartnerCheckout({ acceptTerms }) {
    if (!acceptTerms) {
        toast.error("Please accept the Partner Terms & Refund Policy first.");
        return { ok: false };
    }
    let orderRes;
    try {
        const { data } = await api.post("/partner/create-order", { accept_terms: true });
        orderRes = data;
    } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not create Partner order");
        return { ok: false };
    }

    try {
        await loadScript();
    } catch {
        toast.error("Could not load payment gateway. Please refresh and try again.");
        return { ok: false };
    }

    return new Promise((resolve) => {
        const opts = {
            key: orderRes.key_id,
            amount: orderRes.amount,
            currency: orderRes.currency,
            name: "Global Property Mart",
            description: `${orderRes.package.name} — 12-month onboarding package`,
            image: "/gpm-logo.png",
            order_id: orderRes.order_id,
            prefill: {
                name: orderRes.user?.name || "",
                email: orderRes.user?.email || "",
                contact: orderRes.user?.phone || "",
            },
            theme: { color: "#DAA520" },
            notes: { kind: "gpm_partner_onboarding", package_id: orderRes.package.id },
            handler: async (response) => {
                try {
                    const verify = await api.post("/partner/verify-payment", {
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                    });
                    try {
                        trackMeta("Purchase",
                            { value: orderRes.amount / 100, currency: "INR", content_name: "gpm_partner_package", content_ids: [orderRes.package.id], num_items: 1 },
                            { email: orderRes.user?.email, phone: orderRes.user?.phone }
                        );
                    } catch { /* silent */ }
                    toast.success("Welcome to GPM Partner Program!");
                    resolve({ ok: true, ...verify.data });
                } catch (err) {
                    toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed");
                    resolve({ ok: false });
                }
            },
            modal: {
                ondismiss: () => {
                    toast("Payment cancelled — you can complete it anytime from /partner/activate");
                    resolve({ ok: false, cancelled: true });
                },
            },
        };
        const rzp = new window.Razorpay(opts);
        rzp.on("payment.failed", (resp) => {
            const reason = resp?.error?.description || "Payment failed";
            toast.error(reason);
            resolve({ ok: false, error: reason });
        });
        rzp.open();
    });
}

